/**
 * 채팅 메시지 처리 순수 함수들
 * 메시지 추가, 업데이트, 스트리밍 처리 등
 */

import type { ChatMessageType } from "@/features/text-chat-with-agent/types/chat-message";
import type { SttResultMessage, ChunkMessage } from "../types/websocket-messages";
import { stripEmotionTagsForDisplay } from "@/shared/lib/emotion-tags";

/**
 * 사용자 메시지 추가
 */
export function addUserMessage(
  messages: ChatMessageType[],
  message: string,
  turnId?: string
): ChatMessageType[] {
  return [
    ...messages,
    {
      sender: "user" as const,
      message,
      ...(turnId && { turnId }),
    },
  ];
}

/**
 * 에이전트 스트리밍 메시지 추가 (시작)
 */
export function addStreamingAgentMessage(
  messages: ChatMessageType[],
  message: string,
  turnId: string
): ChatMessageType[] {
  return [
    ...messages,
    {
      sender: "agent" as const,
      message: stripEmotionTagsForDisplay(message),
      isStreaming: true,
      turnId,
    },
  ];
}

/**
 * 에이전트 스트리밍 메시지 업데이트 (진행 중)
 */
export function updateStreamingAgentMessage(
  messages: ChatMessageType[],
  message: string,
  turnId: string
): ChatMessageType[] {
  return messages.map((msg) =>
    msg.turnId === turnId && msg.isStreaming
      ? { ...msg, message: stripEmotionTagsForDisplay(message) }
      : msg
  );
}

/**
 * 에이전트 스트리밍 메시지 완료
 */
export function finalizeStreamingMessage(
  messages: ChatMessageType[],
  message: string,
  turnId: string,
  contextUsed?: ChatMessageType['context_used']
): ChatMessageType[] {
  return messages.map((msg) =>
    msg.turnId === turnId && msg.isStreaming
      ? {
          ...msg,
          message: stripEmotionTagsForDisplay(message),
          isStreaming: false,
          ...(contextUsed && { context_used: contextUsed }),
        }
      : msg
  );
}

/**
 * STT 결과로 사용자 메시지 추가
 */
export function addSttUserMessage(
  messages: ChatMessageType[],
  sttResult: SttResultMessage
): ChatMessageType[] | null {
  if (!sttResult.text?.trim()) return null;

  return [
    ...messages,
    {
      sender: "user" as const,
      message: sttResult.text,
      turnId: sttResult.turn_id,
    },
  ];
}

/**
 * 청크로 에이전트 메시지 추가 (중복 방지)
 */
export function addChunkAgentMessage(
  messages: ChatMessageType[],
  chunk: ChunkMessage
): ChatMessageType[] | null {
  const fullText = chunk.full_text?.trim();
  if (!fullText) return null;

  // 이미 같은 turnId의 에이전트 메시지가 있는지 확인
  const existingMessage = messages.find(
    (msg) => msg.turnId === chunk.turn_id && msg.sender === "agent"
  );

  if (existingMessage) return null; // 이미 있으면 추가하지 않음

  return [
    ...messages,
    {
      sender: "agent" as const,
      message: stripEmotionTagsForDisplay(fullText),
      turnId: chunk.turn_id,
    },
  ];
}

/**
 * 초기 인사말 설정 또는 업데이트
 */
export function setInitialGreeting(
  messages: ChatMessageType[],
  greetingText: string
): ChatMessageType[] {
  // 첫 번째 메시지가 이미 greeting인지 확인 (turnId가 없고 sender가 agent)
  const firstMessage = messages[0];
  const isFirstMessageGreeting =
    firstMessage && firstMessage.sender === "agent" && !firstMessage.turnId;

  // 이미 greeting 메시지가 있으면 업데이트, 없으면 추가
  if (isFirstMessageGreeting) {
    return [
      { ...firstMessage, message: stripEmotionTagsForDisplay(greetingText) },
      ...messages.slice(1),
    ];
  } else {
    // greeting 메시지를 맨 앞에 추가
    return [
      { sender: "agent" as const, message: stripEmotionTagsForDisplay(greetingText) },
      ...messages,
    ];
  }
}

/**
 * STT 결과에서 사용자 메시지 추출
 */
export function processSttResult(
  messages: ChatMessageType[],
  sttResult: SttResultMessage,
  currentTurnId: string | null
): { updatedMessages: ChatMessageType[]; sttText: string } | null {
  // currentTurnId가 없거나, sttResult의 turn_id가 currentTurnId와 다르면 무시
  if (!currentTurnId || sttResult.turn_id !== currentTurnId) {
    console.warn(
      `🚫 turnId 불일치로 sttResult 무시: current=${currentTurnId}, received=${sttResult.turn_id}`
    );
    return null;
  }

  // stt_result에 텍스트가 있으면 사용자 메시지로 추가
  let updatedMessages = messages;
  if (sttResult.text?.trim()) {
    updatedMessages = [
      ...messages,
      {
        sender: "user" as const,
        message: sttResult.text,
        turnId: sttResult.turn_id,
      },
    ];
  }

  return {
    updatedMessages,
    sttText: sttResult.text,
  };
}
